import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentType,
} from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import type {
  SalesMove,
  SalesMoveActionResult,
  SalesMoveBucket,
} from "@sales-ops-amplify/shared";
import { NOT_RIGHT_REASONS } from "@sales-ops-amplify/shared";
import {
  BarChart3,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Search,
} from "lucide-react";
import { cn } from "@/cn";
import { ApiError } from "@/api/client";
import {
  CreditsChip,
  GlobalSearch,
  NotificationsBell,
  UserMenu,
} from "@/components/top-app-bar";
import { useSalesMoveAction, useSalesMoves } from "@/hooks/useSalesMoves";
import { MobileSheet } from "@/components/mobile-sheet";
import { SalesSummaryStrip } from "./sales-summary-strip";
import { SalesMoveBucketSelector } from "./sales-move-bucket-selector";
import { SalesMoveCard } from "./sales-move-card";
import { SalesMoveDetailPanel } from "./sales-move-detail-panel";
import { SalesMoveEmptyState } from "./sales-move-empty-state";
import { SalesMoveExecutionFeedback } from "./sales-move-execution-feedback";
import { MobileSalesMoveCard } from "./mobile-sales-move-card";
import { SalesMoveEvidenceSection } from "./sales-move-evidence-section";

const HOME_BUCKETS: Array<SalesMoveBucket | "ALL_ACTIVE"> = [
  "ALL_ACTIVE",
  "ACT_NOW",
  "REPLY_NEEDED",
  "READY_TO_ADVANCE",
  "AT_RISK",
  "WAITING",
  "NURTURE",
];
const PAGE_SIZES = [10, 20, 30, 40, 50] as const;

function parseHomeBucket(
  raw: string | null,
): SalesMoveBucket | "ALL_ACTIVE" | null {
  if (!raw) return null;
  return HOME_BUCKETS.includes(raw as SalesMoveBucket | "ALL_ACTIVE")
    ? (raw as SalesMoveBucket | "ALL_ACTIVE")
    : null;
}

function parseCursor(raw: string | null): string | undefined {
  if (raw == null || raw === "" || raw === "0") return undefined;
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 0) return undefined;
  return String(Math.floor(n));
}

function parsePageSize(raw: string | null): (typeof PAGE_SIZES)[number] {
  const size = Number(raw);
  return PAGE_SIZES.includes(size as (typeof PAGE_SIZES)[number])
    ? (size as (typeof PAGE_SIZES)[number])
    : 20;
}

function buildPrimaryHref(move: SalesMove): string {
  const target = move.actionDefinition?.target;
  if (!target?.route) return "/tasks";
  const url = new URL(target.route, "http://local");
  if (target.query) {
    for (const [k, v] of Object.entries(target.query)) url.searchParams.set(k, v);
  } else if (target.opportunityId && target.route === "/proposals") {
    url.searchParams.set("opportunityId", target.opportunityId);
  }
  return `${url.pathname}${url.search}`;
}

type Props = {
  /** Escape hatch when salesCommandHomeEnabled is off (operational pulse, not a second Home). */
  OperationalHomeFallback?: ComponentType;
};

export function SalesCommandHome({ OperationalHomeFallback }: Props) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const bucket =
    parseHomeBucket(searchParams.get("bucket")) ?? "ALL_ACTIVE";
  const selectedId = searchParams.get("move");
  const cursor = parseCursor(searchParams.get("cursor"));
  const pageOffset = cursor ? Number(cursor) : 0;
  const pageSize = parsePageSize(searchParams.get("pageSize"));
  const [feedback, setFeedback] = useState<SalesMoveActionResult | null>(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [delegateOpen, setDelegateOpen] = useState(false);
  const [assigneeId, setAssigneeId] = useState("");
  const [summaryFilter, setSummaryFilter] = useState<
    "moves" | "replies" | "risk" | "exposure" | null
  >(null);
  // Header condenses (search collapses to an icon) once the page top scrolls away.
  const [condensed, setCondensed] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  // Below xl the full inline search can't share a row with the labeled actions,
  // so it stays collapsed to an icon (tablet / narrow desktop) regardless of scroll.
  const [wide, setWide] = useState(true);
  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => {
      const offTop = !entry.isIntersecting;
      setCondensed(offTop);
      if (!offTop) setSearchOpen(false);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const mql = window.matchMedia("(min-width: 1280px)");
    const apply = () => {
      setWide(mql.matches);
      if (!mql.matches) setSearchOpen(false);
    };
    apply();
    mql.addEventListener("change", apply);
    return () => mql.removeEventListener("change", apply);
  }, []);

  // Search shows as an icon (not the full input) when scrolled away, or on
  // tablet where there isn't room for it beside the labeled action buttons.
  const searchAsIcon = condensed || !wide;

  function patchHomeParams(
    patch: {
      move?: string | null;
      bucket?: SalesMoveBucket | "ALL_ACTIVE";
      cursor?: string | null;
      pageSize?: number;
    },
    replace = true,
  ) {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if ("move" in patch) {
          if (patch.move) next.set("move", patch.move);
          else next.delete("move");
        }
        if ("bucket" in patch && patch.bucket) {
          if (patch.bucket === "ALL_ACTIVE") next.delete("bucket");
          else next.set("bucket", patch.bucket);
        }
        if ("cursor" in patch) {
          if (patch.cursor && patch.cursor !== "0") next.set("cursor", patch.cursor);
          else next.delete("cursor");
        }
        if ("pageSize" in patch && patch.pageSize) {
          if (patch.pageSize === 20) next.delete("pageSize");
          else next.set("pageSize", String(patch.pageSize));
        }
        return next;
      },
      { replace },
    );
  }

  function selectMove(moveId: string) {
    // Preserve bucket + cursor so Back returns to the same page/bucket.
    patchHomeParams({ move: moveId });
  }

  function changeBucket(nextBucket: SalesMoveBucket | "ALL_ACTIVE") {
    patchHomeParams({
      bucket: nextBucket,
      move: null,
      cursor: null,
    });
    setSummaryFilter(null);
  }

  function goToPage(nextOffset: number, preferredMoveId?: string | null) {
    const safe = Math.max(0, nextOffset);
    patchHomeParams({
      cursor: safe > 0 ? String(safe) : null,
      move: preferredMoveId ?? null,
    });
  }

  function changePageSize(nextSize: number) {
    patchHomeParams({
      pageSize: nextSize,
      cursor: null,
      move: null,
    });
  }

  const query = useSalesMoves({
    bucket,
    limit: pageSize,
    cursor,
    focused:
      bucket === "ALL_ACTIVE" ||
      bucket === "ACT_NOW" ||
      bucket === "REPLY_NEEDED" ||
      bucket === "READY_TO_ADVANCE" ||
      bucket === "AT_RISK",
  });
  const actionMut = useSalesMoveAction();

  const data = query.data;
  const moves = data?.moves ?? [];
  const selected = moves.find((m) => m.id === selectedId) ?? null;
  const mobileIndex = (() => {
    const idx = moves.findIndex((m) => m.id === selectedId);
    return idx >= 0 ? idx : 0;
  })();
  const totalEligible = data?.totalEligible ?? 0;
  const hasPrevPage = pageOffset > 0;
  const hasNextPage = Boolean(data?.nextCursor);
  const pageStart = moves.length ? pageOffset + 1 : 0;
  const pageEnd = pageOffset + moves.length;

  // Restore selection within the current page; never jump buckets/pages on Back.
  useEffect(() => {
    if (query.isLoading || query.isFetching) return;
    if (!moves.length) return;
    if (selectedId && moves.some((m) => m.id === selectedId)) return;
    // If the selected move disappeared, return to the top of its bucket.
    if (selectedId && pageOffset > 0) {
      patchHomeParams({ cursor: null, move: null });
      return;
    }
    patchHomeParams({ move: moves[0]?.id ?? null });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync only when page contents / selection change
  }, [moves, selectedId, query.isLoading, query.isFetching]);

  const emptyVariant = useMemo(() => {
    if (!data?.featureEnabled) return null;
    if (moves.length === 0 && bucket === "ALL_ACTIVE" && pageOffset === 0) {
      return "all_clear" as const;
    }
    if (moves.length === 0) return "bucket_empty" as const;
    return null;
  }, [data, bucket, moves.length, pageOffset]);

  async function runAction(
    move: SalesMove,
    action: Parameters<typeof actionMut.mutateAsync>[0]["body"]["action"],
    payload?: Parameters<typeof actionMut.mutateAsync>[0]["body"]["payload"],
  ) {
    try {
      const result = await actionMut.mutateAsync({
        id: move.id,
        idempotencyKey: crypto.randomUUID(),
        body: {
          action,
          fingerprint: move.fingerprint,
          generationId: data?.generationId ?? "",
          materialStateHash: move.materialStateHash,
          payload,
        },
      });
      setFeedback(result);
      await query.refetch();
      if (result.nextMoveId) {
        selectMove(result.nextMoveId);
      }
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        const body = err.body as { refreshedMove?: SalesMove; message?: string };
        setFeedback({
          success: false,
          localOutcome: "STALE",
          message:
            body.message ??
            "This recommendation changed because newer activity was detected.",
          sideEffects: [],
          refreshedMove: body.refreshedMove,
        });
        await query.refetch();
        return;
      }
      setFeedback({
        success: false,
        localOutcome: "UNCHANGED",
        message: err instanceof Error ? err.message : "Action failed",
        sideEffects: [],
      });
    }
  }

  function onPrimary(move: SalesMove) {
    selectMove(move.id);
    const mode = move.actionDefinition?.mode ?? "NAVIGATE";
    if (mode === "NAVIGATE") {
      void runAction(move, "EXECUTE_PRIMARY");
      navigate(buildPrimaryHref(move));
      return;
    }
    if (move.actionDefinition?.confirmationRequired) {
      const ok = window.confirm(
        `${move.recommendedAction}\n\nThis updates Sales Ops locally. External CRM sync may be simulated.`,
      );
      if (!ok) return;
    }
    void runAction(move, "EXECUTE_PRIMARY");
  }

  function onAskAi(move: SalesMove) {
    selectMove(move.id);
    const params = new URLSearchParams({
      salesMoveId: move.id,
      generationId: data?.generationId ?? "",
      originBucket: bucket,
      originPageSize: String(pageSize),
    });
    if (cursor) params.set("originCursor", cursor);
    navigate(`/ai-assistant?${params.toString()}`);
  }

  function advanceMobileSelection() {
    if (mobileIndex < moves.length - 1) {
      const next = moves[mobileIndex + 1];
      if (next) selectMove(next.id);
      return;
    }
    if (data?.nextCursor) {
      goToPage(Number(data.nextCursor), null);
    }
  }

  const detailMove = selected ?? moves[0] ?? null;
  const lastRefreshed = data?.sourceRefreshedAt || data?.generatedAt;
  const mobileMove =
    moves[Math.min(mobileIndex, Math.max(moves.length - 1, 0))] ?? null;

  // Must run after all hooks — early return above useEffect violated Rules of Hooks.
  if (
    query.isSuccess &&
    data &&
    !data.featureEnabled &&
    OperationalHomeFallback
  ) {
    return <OperationalHomeFallback />;
  }

  return (
    <>
      <div ref={sentinelRef} aria-hidden className="h-px" />
      <header
        className={cn(
          "top-0 z-20 -mx-4 mb-5 flex flex-wrap items-center justify-between gap-3 px-4 transition-colors sm:-mx-6 sm:px-6 md:sticky md:-mx-8 md:px-8",
          // Page top spacing lives here (not on the scroll container) so the
          // sticky header pins flush to the scrollport when condensed.
          condensed
            ? "bg-bg/95 py-2.5 backdrop-blur supports-[backdrop-filter]:bg-bg/85"
            : "bg-transparent pb-1 pt-6 sm:pt-8 md:pt-10",
        )}
      >
        <div>
          <h1
            className={cn(
              "font-bold text-foreground",
              condensed ? "text-xl" : "text-3xl",
            )}
          >
            Today
          </h1>
          {!condensed && (
            <p className="mt-1 text-muted-foreground">
              Your highest-impact sales moves
            </p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          {lastRefreshed && !condensed ? (
            <span className="hidden sm:inline">
              Updated {new Date(lastRefreshed).toLocaleTimeString()}
            </span>
          ) : null}
          <div className="hidden md:block">
            {searchAsIcon && !searchOpen ? (
              <button
                type="button"
                onClick={() => setSearchOpen(true)}
                aria-label="Open search"
                title="Search"
                className="inline-flex items-center rounded-md border border-border px-2.5 py-1.5 font-medium text-foreground hover:bg-muted/20"
              >
                <Search className="h-3.5 w-3.5" />
              </button>
            ) : (
              <GlobalSearch
                className="w-56 xl:w-72"
                autoFocus={searchAsIcon && searchOpen}
              />
            )}
          </div>
          <Link
            to="/dashboard?view=deal-analytics"
            className="inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 font-medium text-foreground hover:bg-muted/20"
          >
            <BarChart3 className="h-3.5 w-3.5" />
            Open Dashboard
          </Link>
          <button
            type="button"
            onClick={() => void query.refetch()}
            className="inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 font-medium text-foreground hover:bg-muted/20"
          >
            <RefreshCw
              className={`h-3.5 w-3.5 ${query.isFetching ? "animate-spin" : ""}`}
            />
            Refresh
          </button>
          <CreditsChip className="hidden lg:inline-flex" />
          <NotificationsBell className="hidden md:inline-flex" />
          <UserMenu className="hidden md:block" />
        </div>
      </header>

      {query.isLoading ? (
        <div className="flex items-center justify-center py-16">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-muted border-t-brand" />
        </div>
      ) : null}

      {query.error && !query.isLoading ? (
        <div className="mb-4 rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          Failed to load sales moves. Please try again.
        </div>
      ) : null}

      {data && !query.isLoading ? (
        <>
          <SalesSummaryStrip
            summary={data.summary}
            activeFilter={summaryFilter}
            onFilter={(filter, nextBucket) => {
              setSummaryFilter(filter);
              if (nextBucket) changeBucket(nextBucket);
            }}
            onNavigate={(key) => {
              if (key === "risk") {
                navigate("/deals?moveFilter=at_risk");
                return;
              }
              if (key === "exposure") {
                navigate("/dashboard?view=deal-analytics");
              }
            }}
          />

          <SalesMoveBucketSelector
            value={bucket}
            counts={data.bucketCounts}
            onChange={changeBucket}
          />

          <SalesMoveExecutionFeedback
            result={feedback}
            onDismiss={() => setFeedback(null)}
            onUndo={() => {
              if (!feedback?.undo?.token || !detailMove) return;
              void runAction(detailMove, "UNDO", {
                undoToken: feedback.undo.token,
              });
            }}
          />

          {/* Desktop / tablet — one page scroll; detail remains visible beside the list. */}
          <div className="hidden md:block">
            {emptyVariant ? (
              <SalesMoveEmptyState variant={emptyVariant} bucket={bucket} />
            ) : (
              <div className="grid items-start gap-5 lg:grid-cols-[minmax(0,7fr)_minmax(420px,5fr)]">
                <div className="min-w-0 space-y-2">
                  <div className="mb-2 flex items-center justify-end gap-2 text-xs text-muted-foreground">
                    <label htmlFor="sales-move-page-size">Show</label>
                    <select
                      id="sales-move-page-size"
                      value={pageSize}
                      onChange={(event) =>
                        changePageSize(Number(event.target.value))
                      }
                      className="rounded-md border border-border bg-background px-2 py-1.5 text-xs font-medium text-foreground"
                    >
                      {PAGE_SIZES.map((size) => (
                        <option key={size} value={size}>
                          {size}
                        </option>
                      ))}
                    </select>
                    <span>items</span>
                  </div>
                  {moves.map((move) => (
                    <SalesMoveCard
                      key={move.id}
                      move={move}
                      selected={detailMove?.id === move.id}
                      onSelect={() => selectMove(move.id)}
                    />
                  ))}
                  <div className="mt-3 flex items-center justify-between gap-3 border-t border-border pt-3 text-xs text-muted-foreground">
                    <button
                      type="button"
                      disabled={!hasPrevPage || query.isFetching}
                      onClick={() =>
                        goToPage(Math.max(0, pageOffset - pageSize), null)
                      }
                      className="inline-flex items-center gap-1 rounded-md border border-border px-2.5 py-1.5 font-medium text-foreground hover:bg-muted/20 disabled:opacity-40"
                    >
                      <ChevronLeft className="h-3.5 w-3.5" />
                      Previous
                    </button>
                    <span className="tabular-nums">
                      {pageStart}–{pageEnd} of {totalEligible}
                    </span>
                    <button
                      type="button"
                      disabled={!hasNextPage || query.isFetching}
                      onClick={() => goToPage(Number(data.nextCursor), null)}
                      className="inline-flex items-center gap-1 rounded-md border border-border px-2.5 py-1.5 font-medium text-foreground hover:bg-muted/20 disabled:opacity-40"
                    >
                      Next
                      <ChevronRight className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
                {detailMove ? (
                  <div className="min-w-0 lg:sticky lg:top-4 lg:self-start">
                    <SalesMoveDetailPanel
                      move={detailMove}
                      busy={actionMut.isPending}
                      onPrimary={() => onPrimary(detailMove)}
                      onLater={(preset) =>
                        void runAction(detailMove, "LATER", {
                          laterPreset: preset,
                        })
                      }
                      onNotRight={(reason) =>
                        void runAction(detailMove, "NOT_RIGHT", { reason })
                      }
                      onDismiss={() =>
                        void runAction(detailMove, "DISMISS", {
                          reason: "Already handled",
                        })
                      }
                      onAskAi={() => onAskAi(detailMove)}
                      onDelegate={() => setDelegateOpen(true)}
                    />
                  </div>
                ) : null}
              </div>
            )}
          </div>

          {/* Mobile */}
          <div className="md:hidden">
            {emptyVariant || !mobileMove ? (
              <SalesMoveEmptyState
                variant={emptyVariant ?? "all_clear"}
                bucket={bucket}
              />
            ) : (
              <>
                <MobileSalesMoveCard
                  move={mobileMove}
                  index={pageOffset + mobileIndex}
                  totalEligible={totalEligible}
                  busy={actionMut.isPending}
                  onPrimary={() => onPrimary(mobileMove)}
                  onReview={() => {
                    selectMove(mobileMove.id);
                    setReviewOpen(true);
                  }}
                  onLater={() =>
                    void runAction(mobileMove, "LATER", {
                      laterPreset: "later_today",
                    }).then(() => {
                      advanceMobileSelection();
                    })
                  }
                  onMore={() => {
                    selectMove(mobileMove.id);
                    setMoreOpen(true);
                  }}
                />
                {(hasPrevPage || hasNextPage) && (
                  <div className="mt-3 flex items-center justify-between gap-3 text-xs text-muted-foreground">
                    <button
                      type="button"
                      disabled={!hasPrevPage || query.isFetching}
                      onClick={() =>
                        goToPage(Math.max(0, pageOffset - pageSize), null)
                      }
                      className="inline-flex items-center gap-1 rounded-md border border-border px-2.5 py-1.5 font-medium text-foreground disabled:opacity-40"
                    >
                      <ChevronLeft className="h-3.5 w-3.5" />
                      Prev page
                    </button>
                    <span className="tabular-nums">
                      {pageStart}–{pageEnd} / {totalEligible}
                    </span>
                    <button
                      type="button"
                      disabled={!hasNextPage || query.isFetching}
                      onClick={() => goToPage(Number(data.nextCursor), null)}
                      className="inline-flex items-center gap-1 rounded-md border border-border px-2.5 py-1.5 font-medium text-foreground disabled:opacity-40"
                    >
                      Next page
                      <ChevronRight className="h-3.5 w-3.5" />
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </>
      ) : null}

      <MobileSheet
        open={reviewOpen && !!detailMove}
        onClose={() => setReviewOpen(false)}
        title="Review move"
        variant="bottom"
      >
        {detailMove ? (
          <div className="space-y-3 p-1 text-sm">
            <p className="font-semibold text-foreground">
              {detailMove.recommendedAction}
            </p>
            <p>{detailMove.situationSummary}</p>
            <p className="text-muted-foreground">{detailMove.whyNow}</p>
            {detailMove.expectedOutcome ? (
              <p>{detailMove.expectedOutcome}</p>
            ) : null}
            <SalesMoveEvidenceSection move={detailMove} />
          </div>
        ) : null}
      </MobileSheet>

      <MobileSheet
        open={moreOpen && !!detailMove}
        onClose={() => setMoreOpen(false)}
        title="More"
        variant="bottom"
      >
        {detailMove ? (
          <div className="flex flex-col gap-2 p-1">
            <button
              type="button"
              className="rounded-md border border-border px-3 py-2.5 text-left text-sm"
              onClick={() => {
                setMoreOpen(false);
                setDelegateOpen(true);
              }}
            >
              Delegate
            </button>
            <button
              type="button"
              className="rounded-md border border-border px-3 py-2.5 text-left text-sm"
              onClick={() => {
                setMoreOpen(false);
                onAskAi(detailMove);
              }}
            >
              Ask AI
            </button>
            <div className="pt-2 text-xs font-medium text-muted-foreground">
              Not right
            </div>
            {NOT_RIGHT_REASONS.map((reason) => (
              <button
                key={reason}
                type="button"
                className="rounded-md border border-border px-3 py-2 text-left text-sm"
                onClick={() => {
                  setMoreOpen(false);
                  void runAction(detailMove, "NOT_RIGHT", { reason }).then(
                    () => {
                      advanceMobileSelection();
                    },
                  );
                }}
              >
                {reason}
              </button>
            ))}
          </div>
        ) : null}
      </MobileSheet>

      <MobileSheet
        open={delegateOpen && !!detailMove}
        onClose={() => setDelegateOpen(false)}
        title="Delegate"
        variant="bottom"
      >
        {detailMove ? (
          <div className="space-y-3 p-1">
            <label className="block text-sm">
              <span className="text-muted-foreground">Assignee user id</span>
              <input
                value={assigneeId}
                onChange={(e) => setAssigneeId(e.target.value)}
                className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                placeholder="teammate-user-id"
              />
            </label>
            <button
              type="button"
              disabled={!assigneeId.trim() || actionMut.isPending}
              className="w-full rounded-md bg-brand px-3 py-2.5 text-sm font-medium text-brand-foreground disabled:opacity-50"
              onClick={() => {
                void runAction(detailMove, "DELEGATE", {
                  delegation: { assigneeId: assigneeId.trim() },
                }).then(() => {
                  setDelegateOpen(false);
                  setAssigneeId("");
                });
              }}
            >
              Delegate move
            </button>
          </div>
        ) : null}
      </MobileSheet>
    </>
  );
}
